import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StepItem } from './StepItem';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

// Helper function to renumber steps after removal or reordering
const renumberSteps = (steps: Step[]): Step[] => {
  return steps.map((step, index) => ({
    ...step,
    step_number: index + 1,
    substeps: step.substeps
      ? step.substeps.map((substep, subIndex) => ({
          ...substep,
          step_number: subIndex + 1,
        }))
      : undefined,
  }));
};

export interface ImageItem {
  url: string;
  order: number;
}

export interface Step {
  id?: string;
  tempId: string;
  step_number: number;
  title: string;
  description: string;
  image_url: ImageItem[];
  tools_equipment_image_url?: ImageItem[];
  is_quality_check: boolean;
  evidence_required: boolean;
  parent_step_id?: string | null;
  part_number?: string | null;
  tools_equipment?: string | null;
  ppe?: string | null;
  required_roles?: string[] | null;
  substeps?: Step[];
}

export type ImageTarget = {
  tempId: string;
  type: 'main' | 'tools';
};

interface StepEditorProps {
  steps: Step[];
  onStepsChange: (steps: Step[]) => void;
  uploading: Set<string>;
  onImageUpload: (tempId: string, file: File, isToolsImage?: boolean) => Promise<void>;
  stepRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  onImageTargetFocus?: (target: ImageTarget) => void;
  onPasteImage?: (target: ImageTarget) => void | Promise<void>;
}

export const StepEditor = ({
  steps,
  onStepsChange,
  uploading,
  onImageUpload,
  stepRefs,
  onImageTargetFocus,
  onPasteImage,
}: StepEditorProps) => {
  const { t } = useTranslation();

  const handleAddStep = () => {
    const tempId = `temp-${Date.now()}`;
    const newStep: Step = {
      tempId,
      step_number: steps.length + 1,
      title: '',
      description: '',
      image_url: [],
      tools_equipment_image_url: [],
      is_quality_check: false,
      evidence_required: false,
      part_number: null,
      tools_equipment: null,
      ppe: null,
    };
    onStepsChange([...steps, newStep]);
  };

  const handleAddSubstep = (parentTempId: string) => {
    const newSteps = [...steps];
    const parentIndex = newSteps.findIndex(s => s.tempId === parentTempId);
    
    if (parentIndex === -1) return;

    const parent = newSteps[parentIndex];
    const substeps = parent.substeps || [];
    const tempId = `temp-${Date.now()}`;
    
    const newSubstep: Step = {
      tempId,
      step_number: substeps.length + 1,
      title: '',
      description: '',
      image_url: [],
      tools_equipment_image_url: [],
      is_quality_check: false,
      evidence_required: false,
      parent_step_id: parentTempId,
      part_number: null,
      tools_equipment: null,
      ppe: null,
    };

    newSteps[parentIndex] = {
      ...parent,
      substeps: [...substeps, newSubstep],
    };

    onStepsChange(newSteps);
  };

  const handleRemoveStep = (tempId: string) => {
    const removeFromTree = (steps: Step[]): Step[] => {
      return steps
        .filter(s => s.tempId !== tempId)
        .map(s => ({
          ...s,
          substeps: s.substeps ? removeFromTree(s.substeps) : undefined,
        }));
    };

    const newSteps = removeFromTree(steps);
    onStepsChange(renumberSteps(newSteps));
  };

  const handleStepChange = (tempId: string, field: keyof Step, value: any) => {
    const updateInTree = (steps: Step[]): Step[] => {
      return steps.map(step => {
        if (step.tempId === tempId) {
          return { ...step, [field]: value };
        }
        if (step.substeps) {
          return {
            ...step,
            substeps: updateInTree(step.substeps),
          };
        }
        return step;
      });
    };

    onStepsChange(updateInTree(steps));
  };

  const handleMakeIndependent = (tempId: string) => {
    const newSteps = [...steps];
    let substepToMove: Step | null = null;

    // Find and remove the substep
    newSteps.forEach(step => {
      if (step.substeps) {
        const substepIndex = step.substeps.findIndex(s => s.tempId === tempId);
        if (substepIndex !== -1) {
          substepToMove = step.substeps[substepIndex];
          step.substeps = step.substeps.filter(s => s.tempId !== tempId);
        }
      }
    });

    if (substepToMove) {
      // Add as main step
      substepToMove.parent_step_id = undefined;
      newSteps.push(substepToMove);
    }

    onStepsChange(renumberSteps(newSteps));
  };

  const handleMakeSubstep = (tempId: string, parentTempId: string) => {
    const newSteps = [...steps];
    const stepIndex = newSteps.findIndex(s => s.tempId === tempId);
    const parentIndex = newSteps.findIndex(s => s.tempId === parentTempId);

    if (stepIndex === -1 || parentIndex === -1 || stepIndex === parentIndex) return;

    const stepToMove = { ...newSteps[stepIndex] };
    newSteps.splice(stepIndex, 1);

    stepToMove.parent_step_id = parentTempId;
    const parent = newSteps[parentIndex > stepIndex ? parentIndex - 1 : parentIndex];
    parent.substeps = [...(parent.substeps || []), stepToMove];

    onStepsChange(renumberSteps(newSteps));
  };

  const registerStepRef = useCallback(
    (tempId: string, el: HTMLDivElement | null) => {
      if (!stepRefs) return;
      if (el) {
        stepRefs.current[tempId] = el;
      } else {
        delete stepRefs.current[tempId];
      }
    },
    [stepRefs],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{t('editor.instructionSteps')}</h2>
        <Button onClick={handleAddStep}>
          <Plus className="mr-2 h-4 w-4" />
          {t('editor.addStep')}
        </Button>
      </div>

      {steps.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
          <p className="text-muted-foreground mb-4">
            {t('editor.noSteps')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {steps.map((step) => (
            <StepItem
              key={step.tempId}
              step={step}
              steps={steps}
              uploading={uploading}
              onStepChange={handleStepChange}
              onRemove={handleRemoveStep}
              onAddSubstep={handleAddSubstep}
              onMakeIndependent={handleMakeIndependent}
              onMakeSubstep={handleMakeSubstep}
              onImageUpload={onImageUpload}
              registerStepRef={registerStepRef}
              onImageTargetFocus={onImageTargetFocus}
              onPasteImage={onPasteImage}
              enableDragReorder={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};
